import type {
  ServicePackageType,
  ContainerTypeSegment,
} from '@/graphql/generatedTypes';
import type { ServicePackageRevisionFragmentFragment } from '@/graphql/fragments/servicePackageRevision.generated';
import type { ServicePackageFragmentFragment } from '@/graphql/fragments/servicePackage.generated';
import i18n from '@/utils/i18n';

// Use the fragment-generated types so helpers accept what queries actually return
// (fragment selects only a subset of ItemType, which differs from the base generatedType)
export type ServicePackage = ServicePackageFragmentFragment;
export type ServicePackageRevision = ServicePackageRevisionFragmentFragment;

const t = (key: string) => i18n.t(`ServicePackage:${key}`);

export const PACKAGE_TYPE_LABELS: Record<ServicePackageType, string> = {
  get basic() {
    return t('basic');
  },
  get essential() {
    return t('essential');
  },
  get pro_plus() {
    return t('proPlus');
  },
  get enterprise() {
    return t('enterprise');
  },
};

export const SEGMENT_LABELS: Record<ContainerTypeSegment, string> = {
  get FOOD() {
    return t('food');
  },
  get DRINKS() {
    return t('drinks');
  },
  get CUTLERY() {
    return t('cutlery');
  },
};

// ── Form types ────────────────────────────────────────────────────────────────

export type AllowanceFormEntry = {
  item_type_id: number;
  item_type_name: string;
  included_amount: string;
};

export type FreeCheckoutFormEntry = {
  segment: ContainerTypeSegment;
  amount: string;
};

export type RevisionFormState = {
  monthly_service_fee_in_cents: string;
  onboarding_fee_in_cents: string;
  ops_order_price_in_cents: string;
  free_checkouts: FreeCheckoutFormEntry[];
  note: string;
  asset_allowances: AllowanceFormEntry[];
};

export function emptyRevisionForm(): RevisionFormState {
  return {
    monthly_service_fee_in_cents: '',
    onboarding_fee_in_cents: '0',
    ops_order_price_in_cents: '',
    free_checkouts: [],
    note: '',
    asset_allowances: [],
  };
}

export function revisionToForm(r: ServicePackageRevision): RevisionFormState {
  return {
    monthly_service_fee_in_cents: String(r.monthly_service_fee_in_cents),
    onboarding_fee_in_cents: String(r.onboarding_fee_in_cents),
    ops_order_price_in_cents: String(r.ops_order_price_in_cents),
    free_checkouts: r.free_checkouts.map((fc) => ({
      segment: fc.segment,
      amount: String(fc.amount),
    })),
    note: r.note ?? '',
    asset_allowances: r.allowances.map((a) => ({
      item_type_id: a.item.id,
      item_type_name: a.item.name,
      included_amount: String(a.monthly_quantity),
    })),
  };
}

export function isRevisionFormValid(f: RevisionFormState): boolean {
  const fields = [
    f.monthly_service_fee_in_cents,
    f.onboarding_fee_in_cents,
    f.ops_order_price_in_cents,
  ];
  if (fields.some((v) => isNaN(parseInt(v, 10)) || parseInt(v, 10) < 0))
    return false;
  if (
    f.free_checkouts.some(
      (fc) => isNaN(parseInt(fc.amount, 10)) || parseInt(fc.amount, 10) < 0,
    )
  )
    return false;
  return f.asset_allowances.every(
    (a) =>
      !isNaN(parseInt(a.included_amount, 10)) &&
      parseInt(a.included_amount, 10) >= 0,
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

export function formatCents(cents: number, currency = 'EUR'): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(cents / 100);
}

export function latestRevisionForTerm(
  revisions: ServicePackageRevision[],
  term: number,
): ServicePackageRevision | undefined {
  const forTerm = revisions.filter((r) => r.min_contract_term_months === term);
  if (forTerm.length === 0) return undefined;
  return forTerm.reduce((best, r) =>
    r.revision_number > best.revision_number ? r : best,
  );
}

export function uniqueTerms(
  revisions: ServicePackageRevision[] = [],
): number[] {
  return [...new Set(revisions.map((r) => r.min_contract_term_months))].sort(
    (a, b) => a - b,
  );
}
